import {AfterViewInit, Component, ElementRef, OnDestroy, ViewChild} from '@angular/core';
import {ModalController} from '@ionic/angular';
import {Organizzazione} from '../../_models/Organizzazione';
import {OrganizzazioniService} from '../../_services/organizzazioni.service';
import {SaveMessage} from '../../_helpers/save-message';
import {AuthenticationService} from '../../_services/authentication.service';
import {TipoGestionale} from '../../_models/generated/tipo-gestionale';

interface SelectItem {
  value: number;
  text: string;
}

@Component({
  selector: 'app-organizzazioni-create',
  templateUrl: './organizzazioni-create.component.html',
  styleUrls: ['./organizzazioni-create.component.scss'],
  standalone: false
})

export class OrganizzazioniCreateComponent implements AfterViewInit, OnDestroy {
  organizzazione: Organizzazione = {
    id: 0,
    codice: '',
    descrizione: '',
    isActive: true,
    codiceGruppo: '',
    tipoInstallazione: 0,
    tipoGestionale: TipoGestionale.Esolver,
    pathFile: '',
    hostFtp: '',
    passwordFtp: '',
    utenteFtp: '',
    indirizzoApiEsolver: '',
    hostApiEsolver: '',
    usernameApiEsolver: '',
    passwordApiEsolver: '',
    esitoAvpMode: 0,
    enableLoginEvents: true,
    enableLaunchOrdiniSpedizione: false
  };
  loading = false;
  readonly tipoGestionale = TipoGestionale;
  codiceTouched = false;

  public tipoInstallazione: SelectItem[] = [
    {value: 1, text: 'LOCALE'},
    {value: 2, text: 'CLOUD'},
    {value: 3, text: 'STANDALONE'}
  ];

  @ViewChild('createForm', {read: ElementRef})
  private createForm?: ElementRef<HTMLElement>;

  private formResizeObserver?: ResizeObserver;
  private modalResizeFrame?: number;

  constructor(
    private saveMessage: SaveMessage,
    private authService: AuthenticationService,
    private organizzazioniService: OrganizzazioniService,
    private modalCtrl: ModalController) { }

  get codiceValido(): boolean {
    return String(this.organizzazione.codice ?? '').trim().length > 0;
  }

  ngAfterViewInit(): void {
    const form = this.createForm?.nativeElement;
    if (!form) return;

    this.scheduleModalHeightSync();
    if (typeof ResizeObserver !== 'undefined') {
      this.formResizeObserver = new ResizeObserver(() => this.scheduleModalHeightSync());
      this.formResizeObserver.observe(form);
    }
  }

  ngOnDestroy(): void {
    this.formResizeObserver?.disconnect();
    if (this.modalResizeFrame !== undefined && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.modalResizeFrame);
    }
  }

  cancel = () => this.modalCtrl.dismiss(false, 'cancel');

  creaOrganizzazione(): void {
    this.organizzazione.codice = String(this.organizzazione.codice ?? '').trim();
    if (!this.codiceValido) {
      this.codiceTouched = true;
      this.saveMessage.messageWarning("Il codice dell'organizzazione è obbligatorio.");
      return;
    }

    this.loading = true;
    this.organizzazioniService.createOrganizzazioneAsync(this.organizzazione)
      .then((_organizzazione: Organizzazione) => this.addAccoutToOrg(_organizzazione))
      .catch(() => this.saveMessage.messageError("Ci sono problemi con la creazione dell'organizzazione."))
      .finally(() => this.loading = false);
  }

  addAccoutToOrg(org: Organizzazione) {
    this.loading = true;
    this.organizzazioniService.addAccountToOrganizzazioneAsync(org.id, this.authService.currentUserValue.id, this.authService.currentUserValue.userName, org.codice)
      .then(() => this.modalCtrl.dismiss(org, 'confirm'))
      .catch(() => this.saveMessage.messageError("Ci sono problemi con il collegamento all'organizzazione."))
      .finally(() => this.loading = false);
  }

  private scheduleModalHeightSync(): void {
    if (this.modalResizeFrame !== undefined && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.modalResizeFrame);
    }

    if (typeof requestAnimationFrame === 'undefined') {
      this.syncModalHeight();
      return;
    }

    this.modalResizeFrame = requestAnimationFrame(() => {
      this.modalResizeFrame = undefined;
      this.syncModalHeight();
    });
  }

  private syncModalHeight(): void {
    const form = this.createForm?.nativeElement;
    if (!form) return;

    const formHeight = Math.max(form.scrollHeight, form.getBoundingClientRect().height);
    void this.modalCtrl.getTop().then(modal => {
      if (!modal) return;

      const wrapper = modal.shadowRoot?.querySelector<HTMLElement>('.modal-wrapper');
      const content = modal.querySelector<HTMLElement>('ion-content');
      const wrapperHeight = wrapper?.getBoundingClientRect().height ?? 0;
      const contentHeight = content?.getBoundingClientRect().height ?? 0;
      const headerHeight = Math.max(0, wrapperHeight - contentHeight) || 3.5 * 16;
      const modalHeight = Math.ceil(formHeight + headerHeight);

      modal.style.setProperty('--organizzazione-create-modal-height', `${modalHeight}px`);
    });
  }
}
